import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { SolarSystem } from '@/scene/SolarSystem'
import { CameraRig } from '@/scene/CameraRig'
import { PointerController } from '@/interaction/PointerController'
import { KonamiListener } from '@/interaction/KonamiListener'
import { IdleWatcher } from '@/interaction/IdleWatcher'
import { AudioManager } from '@/audio/AudioManager'
import { Overlay } from '@/ui/Overlay'
import { InfoCard } from '@/ui/InfoCard'
import { ListView } from '@/ui/ListView'
import { LoadingScreen } from '@/ui/LoadingScreen'
import { sceneConfig } from '@/_constants/scene-config'
import { isMobileDevice } from '@/_utilities/dom'
import type { CelestialBody } from '@/_types/celestial-body'
import type { QualityProfile } from '@/_types/scene'

/**
 * The composition root for the QuickCasa Universe. Wires the renderer, bloom
 * post-processing, scene, camera, input, audio, and overlay UI together, runs the
 * render loop, and owns the flow for entering a service and the easter eggs.
 */
export class UniverseApp {
  private readonly root: HTMLElement
  private readonly quality: QualityProfile
  private readonly renderer: THREE.WebGLRenderer
  private readonly composer: EffectComposer | null
  private readonly bloomPass: UnrealBloomPass | null
  private readonly solarSystem: SolarSystem
  private readonly cameraRig: CameraRig
  private readonly audio: AudioManager
  private readonly overlay: Overlay
  private readonly infoCard: InfoCard
  private readonly listView: ListView
  private readonly pointer: PointerController
  private readonly konami: KonamiListener
  private readonly idle: IdleWatcher
  private readonly clock: THREE.Clock
  private readonly projectionScratch: THREE.Vector3
  private isRunning: boolean
  private isWarping: boolean
  private isPsychedelic: boolean
  private isListOpen: boolean

  /**
   * @param {HTMLElement} root - The element the renderer and overlay mount into.
   */
  public constructor(root: HTMLElement) {
    this.root = root
    this.quality = this.resolveQuality()
    this.clock = new THREE.Clock()
    this.projectionScratch = new THREE.Vector3()
    this.isRunning = false
    this.isWarping = false
    this.isPsychedelic = false
    this.isListOpen = false

    this.renderer = new THREE.WebGLRenderer({
      antialias: !this.quality.isMobile,
      powerPreference: 'high-performance',
    })
    this.renderer.setPixelRatio(this.quality.pixelRatio)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.05
    this.renderer.domElement.classList.add('universe-canvas')
    this.root.append(this.renderer.domElement)

    this.solarSystem = new SolarSystem(this.quality)
    this.cameraRig = new CameraRig(window.innerWidth / window.innerHeight)

    const composition = this.buildComposer()
    this.composer = composition.composer
    this.bloomPass = composition.bloomPass

    this.audio = new AudioManager()
    this.overlay = new Overlay(
      () => this.toggleMute(),
      () => this.toggleView(),
    )
    this.root.append(this.overlay.layer)
    this.infoCard = new InfoCard((body) => this.enterBody(body))
    this.overlay.layer.append(this.infoCard.element)
    this.listView = new ListView(
      (body) => this.enterFromList(body),
      () => this.toggleView(),
    )
    this.root.append(this.listView.element)

    this.pointer = new PointerController(
      this.renderer.domElement,
      this.cameraRig.camera,
      () => this.solarSystem.pickMeshes,
      (id) => this.solarSystem.getBody(id),
      {
        onHover: (body) => this.handleHover(body),
        onActivate: (body) => this.enterBody(body),
        onPointerMove: (normalizedX, normalizedY) => this.cameraRig.setPointer(normalizedX, normalizedY),
      },
    )

    this.konami = new KonamiListener(() => this.togglePsychedelic())
    this.idle = new IdleWatcher(sceneConfig.motion.idleCometDelaySeconds, () =>
      this.solarSystem.launchComet(),
    )

    this.handleResize = this.handleResize.bind(this)
    this.handleWheel = this.handleWheel.bind(this)
    this.tick = this.tick.bind(this)
    window.addEventListener('resize', this.handleResize)
    this.renderer.domElement.addEventListener('wheel', this.handleWheel, { passive: true })

    if (this.quality.isMobile) {
      this.overlay.setHint(
        'Tap a product to learn more, tap Open to launch it. Pinch and drag to explore.',
      )
    }
  }

  /**
   * Present the loading screen, then hand control to the live scene once the user
   * enters. The enter click doubles as the gesture that unlocks audio.
   *
   * @returns {Promise<void>} Resolves once the intro has fully handed over.
   */
  public async start(): Promise<void> {
    const loadingScreen = new LoadingScreen()
    await loadingScreen.run(this.root, () => {
      this.audio.start()
      this.overlay.setMuted(this.audio.getMuted())
      this.cameraRig.playIntro()
      this.overlay.reveal()
      this.beginLoop()
    })
  }

  /**
   * Tear down every listener, timer, and GPU resource.
   *
   * @returns {void}
   */
  public dispose(): void {
    this.isRunning = false
    window.removeEventListener('resize', this.handleResize)
    this.renderer.domElement.removeEventListener('wheel', this.handleWheel)
    this.pointer.dispose()
    this.konami.dispose()
    this.idle.dispose()
    this.renderer.dispose()
  }

  /**
   * Resolve a quality profile from the current device capabilities.
   *
   * @returns {QualityProfile} The chosen quality profile.
   */
  private resolveQuality(): QualityProfile {
    const isMobile = isMobileDevice()
    const ceiling = isMobile
      ? sceneConfig.performance.mobilePixelRatio
      : sceneConfig.performance.maxPixelRatio
    const pixelRatio = Math.min(window.devicePixelRatio, ceiling)
    const starCount = isMobile
      ? Math.floor(sceneConfig.starfield.count / sceneConfig.performance.mobileStarDivisor)
      : sceneConfig.starfield.count
    const ambientParticleCount = isMobile
      ? Math.floor(sceneConfig.particles.ambientCount / sceneConfig.performance.mobileParticleDivisor)
      : sceneConfig.particles.ambientCount
    return {
      isMobile,
      pixelRatio,
      starCount,
      ambientParticleCount,
      isBloomEnabled: !isMobile,
    }
  }

  /**
   * Build the post-processing composer with bloom, or return nulls on devices
   * where bloom is disabled and the scene renders directly.
   *
   * @returns {{ composer: EffectComposer | null; bloomPass: UnrealBloomPass | null }} The composition.
   */
  private buildComposer(): { composer: EffectComposer | null; bloomPass: UnrealBloomPass | null } {
    if (!this.quality.isBloomEnabled) {
      return { composer: null, bloomPass: null }
    }
    const composer = new EffectComposer(this.renderer)
    composer.setPixelRatio(this.quality.pixelRatio)
    composer.setSize(window.innerWidth, window.innerHeight)
    composer.addPass(new RenderPass(this.solarSystem.scene, this.cameraRig.camera))

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      sceneConfig.bloom.strength,
      sceneConfig.bloom.radius,
      sceneConfig.bloom.threshold,
    )
    composer.addPass(bloomPass)
    composer.addPass(new OutputPass())
    return { composer, bloomPass }
  }

  /**
   * Begin the render loop if it is not already running.
   *
   * @returns {void}
   */
  private beginLoop(): void {
    if (this.isRunning) {
      return
    }
    this.isRunning = true
    this.clock.start()
    window.requestAnimationFrame(this.tick)
  }

  /**
   * Render and advance one frame.
   *
   * @returns {void}
   */
  private tick(): void {
    if (!this.isRunning) {
      return
    }
    const deltaSeconds = Math.min(this.clock.getDelta(), 0.05)
    const elapsedSeconds = this.clock.elapsedTime

    this.solarSystem.update(elapsedSeconds, deltaSeconds)
    this.cameraRig.update(deltaSeconds)
    this.updateInfoCardPosition()

    if (this.composer !== null) {
      this.composer.render()
    } else {
      this.renderer.render(this.solarSystem.scene, this.cameraRig.camera)
    }

    window.requestAnimationFrame(this.tick)
  }

  /**
   * Respond to a change in the hovered body: highlight, card, sound, and trails.
   *
   * @param {CelestialBody | null} body - The newly hovered body, or null.
   * @returns {void}
   */
  private handleHover(body: CelestialBody | null): void {
    if (this.isWarping) {
      return
    }
    this.solarSystem.setHighlightedBody(body === null ? null : body.id)
    this.solarSystem.setTrailOpacity(body === null ? 1 : 0.5)
    if (body === null) {
      this.infoCard.hide()
      return
    }
    this.infoCard.show(body)
    this.audio.playHover()
  }

  /**
   * Enter a body: play the warp, dive the camera in, then navigate to its URL.
   *
   * @param {CelestialBody} body - The body to enter.
   * @returns {Promise<void>} Resolves once the warp has launched navigation.
   */
  private async enterBody(body: CelestialBody): Promise<void> {
    if (this.isWarping) {
      return
    }
    this.isWarping = true
    this.pointer.setEnabled(false)
    this.infoCard.hide()
    this.solarSystem.setHighlightedBody(null)
    this.solarSystem.setTrailOpacity(0.15)
    this.audio.playWarp()
    this.overlay.triggerWarpFlash(body.glowColour)

    if (body.tier === 'sun') {
      this.solarSystem.flareSun()
    }

    const target = new THREE.Vector3()
    this.solarSystem.getBodyWorldPosition(body.id, target)
    await this.cameraRig.warpTo(target, sceneConfig.warp.zoomDurationSeconds)
    window.location.assign(body.url)
  }

  /**
   * Toggle between the cosmos (3D) view and the list view. While the list is open
   * the scene keeps rendering behind it but pointer input is suspended.
   *
   * @returns {void}
   */
  private toggleView(): void {
    if (this.isWarping) {
      return
    }
    this.isListOpen = !this.isListOpen
    this.audio.start()
    this.audio.playSelect()
    if (this.isListOpen) {
      this.infoCard.hide()
      this.solarSystem.setHighlightedBody(null)
      this.solarSystem.setTrailOpacity(0.4)
      this.pointer.setEnabled(false)
      this.listView.show()
    } else {
      this.listView.hide()
      this.solarSystem.setTrailOpacity(1)
      this.pointer.setEnabled(true)
    }
  }

  /**
   * Enter a body chosen from the list view: close the list, flip back to the
   * cosmos, and run the standard warp-and-navigate.
   *
   * @param {CelestialBody} body - The body to enter.
   * @returns {void}
   */
  private enterFromList(body: CelestialBody): void {
    this.isListOpen = false
    this.listView.hide()
    void this.enterBody(body)
  }

  /**
   * Toggle audio mute and reflect it on the overlay control.
   *
   * @returns {void}
   */
  private toggleMute(): void {
    this.audio.start()
    this.audio.setMuted(!this.audio.getMuted())
    this.overlay.setMuted(this.audio.getMuted())
  }

  /**
   * Toggle the psychedelic mode unlocked by the Konami code.
   *
   * @returns {void}
   */
  private togglePsychedelic(): void {
    this.isPsychedelic = !this.isPsychedelic
    this.solarSystem.setPsychedelic(this.isPsychedelic ? 1 : 0)
    this.solarSystem.flareSun()
    this.solarSystem.revealAnomaly()
    document.body.classList.toggle('is-psychedelic', this.isPsychedelic)
    if (this.bloomPass !== null) {
      this.bloomPass.strength = this.isPsychedelic
        ? sceneConfig.bloom.strength * 1.8
        : sceneConfig.bloom.strength
    }
    this.audio.playSelect()
  }

  /**
   * Project the active info-card body to screen space and reposition the card.
   *
   * @returns {void}
   */
  private updateInfoCardPosition(): void {
    const body = this.infoCard.getCurrentBody()
    if (body === null) {
      return
    }
    this.solarSystem.getBodyWorldPosition(body.id, this.projectionScratch)
    this.projectionScratch.project(this.cameraRig.camera)
    if (this.projectionScratch.z > 1) {
      return
    }
    const screenX = (this.projectionScratch.x * 0.5 + 0.5) * window.innerWidth
    const screenY = (-this.projectionScratch.y * 0.5 + 0.5) * window.innerHeight
    this.infoCard.setScreenPosition(screenX, screenY)
  }

  /**
   * Apply a viewport resize to the renderer, composer, camera, and bloom pass.
   *
   * @returns {void}
   */
  private handleResize(): void {
    const width = window.innerWidth
    const height = window.innerHeight
    this.renderer.setSize(width, height)
    this.cameraRig.setViewport(width, height)
    if (this.composer !== null) {
      this.composer.setSize(width, height)
    }
    if (this.bloomPass !== null) {
      this.bloomPass.setSize(width, height)
    }
  }

  /**
   * Forward a wheel event to the camera rig as a zoom delta.
   *
   * @param {WheelEvent} event - The wheel event.
   * @returns {void}
   */
  private handleWheel(event: WheelEvent): void {
    this.cameraRig.handleWheel(event.deltaY)
  }
}
